(() => {
  if (window.p01StoryLoaded) return;
  window.p01StoryLoaded = true;
  const instances = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 767px)');

  function mount(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const listeners = { signal: abort.signal };
    const cleanup = [];
    const media = root.querySelector('[data-p01-hero-media]');
    if (media) {
      const video = media.querySelector('[data-p01-hero-video]');
      let visible = false;
      let userPaused = false;
      let currentUrl = '';
      const updateControl = () => {
        const playing = !video.paused;
        media.setAttribute('aria-pressed', String(playing));
        media.setAttribute('aria-label', playing ? media.dataset.pauseLabel : media.dataset.playLabel);
      };
      const play = async () => {
        if (!currentUrl || document.hidden || !visible) return;
        if (!video.getAttribute('src')) video.src = currentUrl;
        video.muted = true;
        try { await video.play(); } catch { updateControl(); }
      };
      const pause = () => video.pause();
      const autoplay = () => {
        if (visible && !document.hidden && !reduced.matches && !userPaused && media.dataset.autoplay === 'true') void play();
        else pause();
      };
      const chooseSource = () => {
        const next = mobile.matches ? media.dataset.mobileVideo : media.dataset.desktopVideo;
        if (next === currentUrl) return;
        pause();
        currentUrl = next || '';
        media.classList.remove('is-video-ready');
        video.removeAttribute('src');
        video.load();
        media.tabIndex = currentUrl ? 0 : -1;
        if (currentUrl) media.setAttribute('role', 'button');
        else media.removeAttribute('role');
        updateControl();
        autoplay();
      };
      const toggle = () => {
        if (video.paused) { userPaused = false; void play(); }
        else { userPaused = true; pause(); }
      };
      media.addEventListener('click', toggle, listeners);
      media.addEventListener('keydown', event => {
        if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); toggle(); }
      }, listeners);
      video.addEventListener('playing', () => { media.classList.add('is-video-ready'); updateControl(); }, listeners);
      video.addEventListener('pause', updateControl, listeners);
      video.addEventListener('error', () => { media.classList.remove('is-video-ready'); updateControl(); }, listeners);
      document.addEventListener('visibilitychange', autoplay, listeners);
      mobile.addEventListener('change', chooseSource, listeners);
      reduced.addEventListener('change', () => { if (reduced.matches) media.classList.remove('is-video-ready'); autoplay(); }, listeners);
      root.addEventListener('p01:section-select', () => { userPaused = true; pause(); }, listeners);
      const observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .2;
        autoplay();
      }, { threshold: [0, .2] });
      observer.observe(media);
      chooseSource();
      cleanup.push(() => { observer.disconnect(); pause(); video.removeAttribute('src'); video.load(); });
    }
    const form = root.querySelector('.p01-feedback__form');
    if (form) {
      const storageKey = `p01-contact:${form.id}`;
      const success = form.querySelector('[data-p01-contact-success]');
      const fields = Array.from(form.querySelectorAll('input:not([type="hidden"]), textarea'));
      const button = form.querySelector('[data-p01-contact-submit]');
      const status = form.querySelector('[data-p01-contact-status]');
      let submitting = false;
      let releaseTimer;
      const clearSaved = () => { try { sessionStorage.removeItem(storageKey); } catch {} };
      if (success) clearSaved();
      else {
        try {
          const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
          if (saved && Date.now() - saved.savedAt < 30 * 60 * 1000) fields.forEach(field => {
            if (typeof saved.values[field.name] === 'string' && !field.value) field.value = saved.values[field.name];
          });
          else clearSaved();
        } catch { clearSaved(); }
      }
      const reset = () => {
        submitting = false;
        clearTimeout(releaseTimer);
        if (button) { button.removeAttribute('aria-disabled'); button.textContent = button.dataset.idleLabel; }
        if (status) status.textContent = '';
      };
      const message = form.querySelector('[data-p01-message]');
      const messageValidity = () => {
        if (message) message.setCustomValidity(message.value.trim() ? '' : message.dataset.requiredMessage);
      };
      fields.forEach(field => {
        field.addEventListener('invalid', () => {
          field.setAttribute('aria-invalid', 'true');
          const error = field.parentElement.querySelector('[data-p01-field-error]');
          if (error) { error.textContent = field.validationMessage; error.hidden = false; }
        }, listeners);
        field.addEventListener('input', () => {
          if (field === message) messageValidity();
          field.removeAttribute('aria-invalid');
          const error = field.parentElement.querySelector('[data-p01-field-error]');
          if (error) error.hidden = true;
        }, listeners);
      });
      if (message) messageValidity();
      form.addEventListener('submit', event => {
        messageValidity();
        if (!form.checkValidity()) { event.preventDefault(); form.reportValidity(); return; }
        if (submitting) { event.preventDefault(); return; }
        try {
          sessionStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), values: Object.fromEntries(fields.map(field => [field.name, field.value])) }));
        } catch {}
        queueMicrotask(() => {
          if (event.defaultPrevented) return;
          submitting = true;
          if (button) { button.setAttribute('aria-disabled', 'true'); button.textContent = button.dataset.sendingLabel; }
          if (status) status.textContent = button.dataset.sendingLabel;
          releaseTimer = setTimeout(reset, 20000);
        });
      }, listeners);
      window.addEventListener('pageshow', reset, listeners);
      cleanup.push(() => clearTimeout(releaseTimer));
    }
    instances.set(root, () => { abort.abort(); cleanup.forEach(fn => fn()); });
  }
  function mountWithin(parent) {
    if (parent.matches?.('[data-p01-story]')) mount(parent);
    parent.querySelectorAll?.('[data-p01-story]').forEach(mount);
  }
  mountWithin(document);
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) if (event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
  });
  document.addEventListener('shopify:section:select', event => {
    event.target.querySelectorAll('[data-p01-story]').forEach(root => root.dispatchEvent(new Event('p01:section-select')));
  });
})();
